import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Badge, Card, ErrorState, Loading, Select, StatCard } from '@/components/ui';
import { formatDateTime, formatNumber, formatPhone } from '@/lib/format';
import { useState } from 'react';

/**
 * The daily-use half of a client profile.
 *
 * The profile was a settings page: plan, prices, contacts, feature flags —
 * everything you set once and never look at again. None of it answers the
 * question that is actually asked about a client, which is "is this account
 * working today, and if not, since when".
 *
 * So: what it just did, what is wrong with it, who is available to take its
 * transfers, and who last changed anything. The four things somebody opens
 * three other pages to assemble when a customer rings up.
 */

interface Summary {
  calls: number;
  answered: number;
  qualified: number;
  transferred: number;
  machines: number;
  dead_air: number;
  minutes: number;
  last_call_at: string | null;
}

interface CallRow {
  id: string;
  caller_number: string | null;
  state: string;
  disposition: string | null;
  started_at: string;
  duration_sec: number | null;
  qualified: boolean;
  transferred: boolean;
  end_reason: string | null;
  vicidial_campaign: string | null;
  bot_name: string | null;
  customer_turns: number;
}

interface AlertRow {
  id: string;
  kind: string;
  severity: string;
  title: string;
  fired_at: string;
  acknowledged_at: string | null;
}

interface Agents {
  total: number;
  enabled: number;
  logged_in: number;
  errored: number;
  checked_at: string | null;
}

interface ChangeRow {
  id: string;
  actor_email: string | null;
  actor_type: string;
  action: string;
  entity: string | null;
  entity_id: string | null;
  created_at: string;
}

interface Activity {
  window_hours: number;
  summary: Summary;
  calls: CallRow[];
  alerts: AlertRow[];
  agents: Agents;
  changes: ChangeRow[];
}

const SEVERITY: Record<string, 'red' | 'amber' | 'blue' | 'gray'> = {
  critical: 'red',
  error: 'red',
  warning: 'amber',
  warn: 'amber',
  info: 'blue',
};

/** What this call came to, in one word, from what survives it. */
function outcome(call: CallRow): { label: string; tone: 'green' | 'amber' | 'gray' | 'red' } {
  if (call.transferred) return { label: 'transferred', tone: 'green' };
  if (call.qualified) return { label: 'qualified, not transferred', tone: 'amber' };
  if (call.end_reason === 'answering_machine') return { label: 'machine', tone: 'gray' };
  if (call.end_reason === 'dead_air') return { label: 'dead air', tone: 'gray' };
  if (call.customer_turns === 0) return { label: 'never spoke', tone: 'gray' };
  return { label: call.disposition ?? call.state.replace(/_/g, ' '), tone: 'gray' };
}

function ago(iso: string | null): string {
  if (!iso) return 'never';
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

export default function ClientActivity({ tenantId }: { tenantId: string }) {
  const [hours, setHours] = useState(24);

  const activity = useQuery({
    queryKey: ['client-activity', tenantId, hours],
    queryFn: () =>
      api.get<Activity>(`/admin/clients/${tenantId}/activity`, { hours }),
    refetchInterval: 60_000,
  });

  if (activity.isPending) return <Loading />;
  if (activity.error)
    return <ErrorState error={activity.error} retry={() => activity.refetch()} />;

  const { summary, calls, alerts, agents, changes } = activity.data!;

  // A qualified lead with nobody to hand it to is the most expensive failure
  // on this platform, so it is said in words rather than left as two numbers
  // to compare.
  const noCover = agents.enabled > 0 && agents.logged_in === 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs text-ink-500">
          Last call {ago(summary.last_call_at)}
        </p>
        <Select
          value={String(hours)}
          onChange={(e) => setHours(Number(e.target.value))}
          className="w-44"
        >
          <option value="24">Last 24 hours</option>
          <option value="72">Last 3 days</option>
          <option value="168">Last 7 days</option>
          <option value="720">Last 30 days</option>
        </Select>
      </div>

      {noCover && (
        <div className="rounded-lg border border-red-300 bg-red-50/60 p-3 text-sm dark:border-red-900 dark:bg-red-950/30">
          <strong>No closer is logged in.</strong> This account has{' '}
          {agents.enabled} enabled {agents.enabled === 1 ? 'agent' : 'agents'} and
          none of them is available, so every lead the bot qualifies right now
          has nowhere to go.
        </div>
      )}

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard label="Calls" value={formatNumber(summary.calls)} />
        <StatCard
          label="Reached a person"
          value={formatNumber(summary.answered - summary.machines - summary.dead_air)}
          sub={`${formatNumber(summary.machines)} machines, ${formatNumber(
            summary.dead_air,
          )} dead air`}
        />
        <StatCard
          label="Qualified"
          value={formatNumber(summary.qualified)}
          tone={summary.qualified > 0 ? 'good' : 'default'}
        />
        <StatCard
          label="Transferred"
          value={formatNumber(summary.transferred)}
          sub={
            summary.qualified > summary.transferred
              ? `${summary.qualified - summary.transferred} qualified did not reach anybody`
              : undefined
          }
          tone={
            summary.qualified > 0 && summary.transferred === 0 ? 'bad' : 'default'
          }
        />
      </div>

      {!!alerts.length && (
        <Card
          title="Open alerts"
          description="This account's own, rather than every account's."
        >
          <ul className="space-y-2">
            {alerts.map((alert) => (
              <li key={alert.id} className="flex flex-wrap items-center gap-2 text-sm">
                <Badge tone={SEVERITY[alert.severity?.toLowerCase()] ?? 'gray'}>
                  {alert.severity}
                </Badge>
                <span className="min-w-0 flex-1">{alert.title}</span>
                {alert.acknowledged_at && <Badge tone="gray">acknowledged</Badge>}
                <span className="text-xs text-ink-500">{ago(alert.fired_at)}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <Card
        title="Closer availability"
        description="Who can take a transfer from this account's bots."
        actions={
          <span className="text-xs text-ink-500">
            checked {ago(agents.checked_at)}
          </span>
        }
      >
        <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm">
          <span>
            Logged in{' '}
            <strong
              className={
                'tabular-nums ' +
                (agents.logged_in
                  ? 'text-green-600 dark:text-green-400'
                  : 'text-red-600 dark:text-red-400')
              }
            >
              {agents.logged_in}
            </strong>{' '}
            <span className="text-ink-500">of {agents.enabled} enabled</span>
          </span>
          {agents.errored > 0 && (
            <span className="text-red-600 dark:text-red-400">
              {agents.errored} could not be checked
            </span>
          )}
          {agents.total > agents.enabled && (
            <span className="text-ink-500">
              {agents.total - agents.enabled} disabled
            </span>
          )}
        </div>
      </Card>

      <Card
        title="Recent calls"
        description="Newest first. Click one to read the transcript."
      >
        {!calls.length ? (
          <p className="text-sm text-ink-500">
            This account has never placed a call.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="glee-table glee-table--compact">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Number</th>
                  <th>Bot</th>
                  <th>Campaign</th>
                  <th>Length</th>
                  <th>Outcome</th>
                </tr>
              </thead>
              <tbody>
                {calls.map((call) => {
                  const result = outcome(call);
                  return (
                    <tr
                      key={call.id}
                     
                    >
                      <td className="text-xs tabular-nums text-ink-500">
                        <Link to={`/admin/calls/${call.id}`} className="hover:underline">
                          {formatDateTime(call.started_at)}
                        </Link>
                      </td>
                      <td className="font-mono text-xs">
                        {call.caller_number ? formatPhone(call.caller_number) : '—'}
                      </td>
                      <td className="text-ink-500">
                        {call.bot_name ?? '—'}
                      </td>
                      <td className="text-ink-500">
                        {call.vicidial_campaign ?? '—'}
                      </td>
                      <td className="tabular-nums text-ink-500">
                        {call.duration_sec ? `${call.duration_sec}s` : '—'}
                      </td>
                      <td>
                        <Badge tone={result.tone}>{result.label}</Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card
        title="Recent changes"
        description="Who last touched this account. &ldquo;It stopped working on Tuesday&rdquo; is answered here."
      >
        {!changes.length ? (
          <p className="text-sm text-ink-500">
            Nothing has been changed on this account since it was created.
          </p>
        ) : (
          <ul className="space-y-1.5 text-sm">
            {changes.map((change) => (
              <li key={change.id} className="flex flex-wrap items-baseline gap-2">
                <span className="tabular-nums text-xs text-ink-500">
                  {formatDateTime(change.created_at)}
                </span>
                <span className="font-medium">{change.action}</span>
                {change.entity && (
                  <span className="text-ink-500">{change.entity}</span>
                )}
                <span className="ml-auto text-xs text-ink-500">
                  {change.actor_email ?? change.actor_type}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
